import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Printer, Map, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { return new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { display: 'flex', alignItems: 'center', gap: 20, borderBottom: '2px solid #1f2937', paddingBottom: 14, marginBottom: 18 },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrInfo: { flex: 1 },
  hdrT1: { fontSize: 20, fontWeight: 700 },
  hdrT2: { fontSize: 13, color: '#374151', marginTop: 3 },
  hdrT3: { fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 },
  th: { borderBottom: '2px solid #d1d5db', padding: '8px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11 },
  thC: { borderBottom: '2px solid #d1d5db', padding: '8px 8px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  thR: { borderBottom: '2px solid #d1d5db', padding: '8px 8px', textAlign: 'right', fontWeight: 700, fontSize: 11 },
  td: { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', verticalAlign: 'middle', fontSize: 12 },
  tdC: { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', textAlign: 'center', verticalAlign: 'middle', fontSize: 12 },
  tdR: { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', textAlign: 'right', verticalAlign: 'middle', fontSize: 12 },
  totHdr: { background: '#f3f4f6', fontWeight: 700, borderTop: '2px solid #1f2937', borderBottom: '1px solid #d1d5db' },
  totRow: { borderBottom: '1px solid #e5e7eb' },
  footer: { textAlign: 'left', fontSize: 11, color: '#6b7280', marginTop: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' },
}

export default function MapaGeral() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [tipo, setTipo] = useState('liquido')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const [mapaConfig, setMapaConfig] = useState(null)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    Promise.all([
      leiloesService.getByOrganizador(orgId),
      mapasService.getConfig(orgId).catch(() => null),
    ]).then(([data, cfg]) => {
      const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
      setLeiloes(sorted)
      if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      if (cfg) setMapaConfig(cfg)
    }).finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setDados(null); return }
    setLoading(true)
    mapasService.getMapaGeralDados(orgId, leilaoId, tipo)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }, [orgId, leilaoId, tipo])

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const handleImprimir = async (modo = 'completo') => {
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getMapaGeralPdf(orgId, leilaoId, tipo, modo)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Map className="w-5 h-5 text-indigo-600" />
        <h1 className="text-lg font-semibold text-gray-800">Mapa Geral</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={v => { setLeilaoId(v); setDados(null) }} disabled={loadingL} className="flex-[2] min-w-[200px]">
            {loadingL ? <option>Carregando...</option> : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </Select>
          <Select label="Tipo" value={tipo} onChange={v => { setTipo(v); setDados(null) }} className="flex-1 min-w-[180px]">
            <option value="liquido">Vendas (valor líquido)</option>
            <option value="comissao">Comissão</option>
          </Select>
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={() => handleImprimir('completo')} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-3 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> Imprimir
            </button>
            <button onClick={() => handleImprimir('vendas')} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-3 py-2 h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> Só Vendas
            </button>
            <button onClick={() => handleImprimir('resumo')} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-3 py-2 h-9 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> Só Resumo
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Selecione um leilão</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div style={S.page}>
            {/* Header */}
            <div style={{ ...S.hdr, justifyContent: 'space-between' }}>
              <div style={{ minWidth: 140 }}>
                {logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700 }}>{organizadorAtivo?.name || ''}</span>}
              </div>
              <div style={{ flex: 1, textAlign: 'center', padding: '0 16px' }}>
                <div style={S.hdrT1}>MAPA GERAL</div>
                <div style={S.hdrT2}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
                <div style={S.hdrT3}>{leilaoAtual?.nome} — {fmtDate(leilaoAtual?.data_leilao)}</div>
                {dados.leiloeiro_nome && <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>Leiloeiro: {dados.leiloeiro_nome}</div>}
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}>
                <img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} />
              </div>
            </div>

            {/* Tabela de lotes — dinâmica baseada na config */}
            {tipo === 'liquido' && dados.lotes && dados.lotes.length > 0 && (() => {
              const vendas = dados.lotes.filter(l => l.situacao === 'VENDA')
              const defesas = dados.lotes.filter(l => l.situacao === 'DEFESA')
              const outros = dados.lotes.filter(l => l.situacao !== 'VENDA' && l.situacao !== 'DEFESA')

              // Colunas dinâmicas
              const separados = mapaConfig?.mapa_geral_vendedor_comprador_separados || false
              const colunasAtivas = mapaConfig?.mapa_geral_colunas_ativas || ['lote', 'vend_comp', 'comissao', 'descricao', 'tipo', 'qtd', 'valor_unitario', 'valor_total', 'valor_liquido']
              const ORDEM = ['lote', 'vend_comp', 'vendedor', 'comprador', 'comissao', 'qtd', 'descricao', 'tipo', 'valor_unitario', 'valor_total', 'valor_liquido']
              let cols = ORDEM.filter(c => colunasAtivas.includes(c))
              if (separados) cols = cols.filter(c => c !== 'vend_comp')
              else cols = cols.filter(c => c !== 'vendedor' && c !== 'comprador')

              const COL_DEF = {
                lote:           { label: 'Lote',           style: S.th },
                vend_comp:      { label: 'Vendedor / Comprador', style: S.th },
                vendedor:       { label: 'Vendedor',       style: S.th },
                comprador:      { label: 'Comprador',      style: S.th },
                comissao:       { label: 'Comissão',       style: S.thC },
                descricao:      { label: 'Descrição',      style: S.th },
                tipo:           { label: 'Tipo',           style: S.thC },
                qtd:            { label: 'Qtd',            style: S.thC },
                valor_unitario: { label: 'V. Unit.',       style: S.thR },
                valor_total:    { label: 'V. Total',       style: S.thR },
                valor_liquido:  { label: 'V. Liq.',        style: S.thR },
              }

              const getCellStyle = (col) => {
                if (['valor_unitario', 'valor_total', 'valor_liquido'].includes(col)) return S.tdR
                if (['comissao', 'tipo', 'qtd'].includes(col)) return S.tdC
                return S.td
              }

              const renderCell = (l, col) => {
                switch (col) {
                  case 'lote': return <strong>{l.numero_lote}</strong>
                  case 'vend_comp': return <><div style={{ fontWeight: 600 }}>{l.vendedor}</div><div>{l.comprador}</div></>
                  case 'vendedor': return l.vendedor
                  case 'comprador': return l.comprador
                  case 'comissao': return l.is_defesa && Number(l.cv_pct) === 0 && l.cv_val
                    ? `V: ${brl(l.cv_val)} (fixo)`
                    : `V:${l.cv_pct}% C:${l.cc_pct}%`
                  case 'descricao': return l.descricao
                  case 'tipo': return l.situacao
                  case 'qtd': return l.qtd
                  case 'valor_unitario': return brl(l.vv_unit)
                  case 'valor_total': return brl(l.vv)
                  case 'valor_liquido': return brl(l.liquido)
                  default: return ''
                }
              }

              const renderSection = (lotes, label, bgColor, borderColor) => (
                <>
                  {lotes.length > 0 && (
                    <tr><td colSpan={cols.length} style={{ background: bgColor, padding: '6px 10px', fontWeight: 700, fontSize: 12, borderBottom: `2px solid ${borderColor}`, textAlign: 'center' }}>{label}</td></tr>
                  )}
                  {lotes.map((l, i) => (
                    <tr key={i}>
                      {cols.map(col => (
                        <td key={col} style={{ ...getCellStyle(col), fontSize: col === 'comissao' || col === 'descricao' ? 11 : 12 }}>
                          {renderCell(l, col)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              )

              return (
              <table style={S.tbl}>
                <thead><tr>
                  {cols.map(col => (
                    <th key={col} style={COL_DEF[col].style}>{COL_DEF[col].label}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {renderSection(vendas, 'VENDAS', '#e0f2fe', '#0ea5e9')}
                  {renderSection(defesas, 'DEFESAS', '#fef3c7', '#f59e0b')}
                  {outros.length > 0 && renderSection(outros, 'NÃO MOVIMENTADOS', '#f3f4f6', '#9ca3af')}
                </tbody>
              </table>
              )
            })()}

            {/* Resumo */}
            <table style={{ ...S.tbl, marginTop: 16 }}>
              <thead><tr style={S.totHdr}>
                <td style={{ ...S.td, fontWeight: 700, width: '40%' }}>TOTAL</td>
                <td style={{ ...S.tdC, fontWeight: 700, width: '10%' }}>%</td>
                <td style={{ ...S.tdC, fontWeight: 700, width: '10%' }}>LOTES</td>
                <td style={{ ...S.tdC, fontWeight: 700, width: '10%' }}>QTD</td>
                <td style={{ ...S.tdR, fontWeight: 700, width: '30%' }}>VALOR</td>
              </tr></thead>
              <tbody>
                <tr style={S.totRow}><td style={S.td}>Vendas</td><td style={S.tdC}>{dados.resumo.pct_vendas}</td><td style={S.tdC}>{dados.resumo.lotes_vendas}</td><td style={S.tdC}>{dados.resumo.qtd_vendas}</td><td style={S.tdR}>{brl(dados.resumo.valor_vendas)}</td></tr>
                <tr style={S.totRow}><td style={S.td}>Defesas</td><td style={S.tdC}>{dados.resumo.pct_defesas}</td><td style={S.tdC}>{dados.resumo.lotes_defesas}</td><td style={S.tdC}>{dados.resumo.qtd_defesas}</td><td style={S.tdR}>{brl(dados.resumo.valor_defesas)}</td></tr>
                <tr style={S.totRow}><td style={S.td}>Indefinidos</td><td style={S.tdC}>0</td><td style={S.tdC}>0</td><td style={S.tdC}>0</td><td style={S.tdR}>{brl(0)}</td></tr>
                <tr style={S.totRow}><td style={S.td}>Comissão compra</td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdR}>{brl(dados.resumo.comissao_compra)}</td></tr>
                <tr style={S.totRow}><td style={S.td}>Comissão venda</td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdR}>{brl(dados.resumo.comissao_venda)}</td></tr>
                <tr style={S.totRow}><td style={S.td}>Comissão defesa</td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdR}>{brl(dados.resumo.comissao_defesa)}</td></tr>
                {dados.resumo.gta_nota > 0 && (
                  <tr style={S.totRow}><td style={S.td}>Despesas (GTA + Nota)</td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdR}>{brl(dados.resumo.gta_nota)}</td></tr>
                )}
                <tr style={S.totRow}><td style={S.td}>Despesas</td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdR}>{brl(dados.resumo.despesas)}</td></tr>
                <tr style={S.totRow}><td style={S.td}>Comissão a receber</td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdR}>{brl(dados.resumo.comissao_pagar)}</td></tr>
                <tr style={{ borderTop: '2px solid #1f2937' }}><td style={{ ...S.td, fontWeight: 700 }}>Líquido</td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdC}></td><td style={{ ...S.tdR, fontWeight: 700 }}>{brl(dados.resumo.liquido)}</td></tr>
              </tbody>
            </table>

            {/* Resumo por Comissão */}
            {dados.resumo_comissoes && dados.resumo_comissoes.length > 0 && (
              <>
                <div style={{ marginTop: 16, fontWeight: 700, fontSize: 13, borderBottom: '2px solid #1f2937', paddingBottom: 4, marginBottom: 8 }}>RESUMO POR COMISSÃO</div>
                <table style={S.tbl}>
                  <thead><tr>
                    <th style={S.th}>Comissão</th>
                    <th style={S.thC}>Lotes</th>
                    <th style={S.thC}>Qtd</th>
                    <th style={S.thR}>Valor Vendas</th>
                    <th style={S.thR}>Valor Comissão</th>
                  </tr></thead>
                  <tbody>
                    {dados.resumo_comissoes.map((rc, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={S.td}><strong>{rc.label}</strong></td>
                        <td style={S.tdC}>{rc.lotes}</td>
                        <td style={S.tdC}>{rc.qtd}</td>
                        <td style={S.tdR}>{brl(rc.valor_vendas)}</td>
                        <td style={S.tdR}>{brl(rc.valor_comissao)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Mapa Geral — {leilaoAtual?.nome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Mapa Geral" style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
